// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { transactionTypeSchema } from '@/project/api/types';
import {
  MAX_YEAR,
  MIN_YEAR,
  YEAR_RANGE_ERROR,
  findPhaseSequenceErrors,
} from '@/project/utilities/validate-line-item-dates';
import { sparseItemSchema, sparseLocationSchema } from '@/sparse/api/types';
import { dateSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

const lineItemSchema = z
  .object({
    id: z.string().optional(),
    item: sparseItemSchema.nullable(),
    quantity: z.number().nullable(),
    details: z.string().nullable(),
    location: sparseLocationSchema.nullable(),
    transactionType: transactionTypeSchema,
    aggregationKey: z.string(),
    startOn: dateSchema.nullable(),
    endOn: dateSchema.nullable(),
    hasInvoicedCharges: z.boolean(),
  })
  .superRefine((lineItem, context) => {
    if (lineItem.item === null) {
      context.addIssue({
        code: 'custom',
        path: ['item'],
        message: 'Item is required',
      });
    }

    if (lineItem.quantity === null) {
      context.addIssue({
        code: 'custom',
        path: ['quantity'],
        message: 'Quantity is required',
      });
    } else if (lineItem.quantity < 0) {
      context.addIssue({
        code: 'custom',
        path: ['quantity'],
        message: 'Quantity cannot be negative',
      });
    }

    // Dates are optional, but if one is set both must be set
    if (lineItem.startOn !== null && lineItem.endOn === null) {
      context.addIssue({
        code: 'custom',
        path: ['endOn'],
        message: 'End date is required for line item phases',
      });
    }

    if (lineItem.endOn !== null && lineItem.startOn === null) {
      context.addIssue({
        code: 'custom',
        path: ['startOn'],
        message: 'Start date is required for line item phases',
      });
    }

    if (lineItem.startOn !== null && lineItem.endOn !== null) {
      // Luxon comparisons on invalid DateTime return false, so invalid
      // date values silently skip this rule rather than surfacing here.
      // End may equal start (single-day phase); reject only end < start.
      if (lineItem.endOn < lineItem.startOn) {
        context.addIssue({
          code: 'custom',
          path: ['endOn'],
          message: 'End date cannot be before the start date',
        });
      }
    }

    // The server refuses a year outside the range, so the form refuses to submit one.
    if (
      lineItem.startOn !== null &&
      (lineItem.startOn.year < MIN_YEAR || lineItem.startOn.year > MAX_YEAR)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['startOn'],
        message: YEAR_RANGE_ERROR,
      });
    }

    if (
      lineItem.endOn !== null &&
      (lineItem.endOn.year < MIN_YEAR || lineItem.endOn.year > MAX_YEAR)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['endOn'],
        message: YEAR_RANGE_ERROR,
      });
    }
  });

export const projectLineItemsFormSchema = z
  .object({ lineItems: z.array(lineItemSchema) })
  .superRefine((data, context) => {
    // Items that share an aggregation key (phases) must have date ranges.
    // Only standalone items (unique key, single in group) can be dateless.
    const keyCounts = new Map<string, number>();
    for (const item of data.lineItems) {
      keyCounts.set(
        item.aggregationKey,
        (keyCounts.get(item.aggregationKey) ?? 0) + 1
      );
    }
    for (let i = 0; i < data.lineItems.length; i++) {
      const item = data.lineItems[i];
      const count = keyCounts.get(item.aggregationKey) ?? 1;
      if (count > 1 && (item.startOn === null || item.endOn === null)) {
        if (item.startOn === null) {
          context.addIssue({
            code: 'custom',
            path: ['lineItems', i, 'startOn'],
            message: 'Start date is required for line item phases',
          });
        }
        if (item.endOn === null) {
          context.addIssue({
            code: 'custom',
            path: ['lineItems', i, 'endOn'],
            message: 'End date is required for line item phases',
          });
        }
      }
    }

    // The server refuses phases that overlap or leave a gap, so the form refuses to submit them.
    const phaseIndicesByKey = new Map<string, number[]>();
    for (let i = 0; i < data.lineItems.length; i++) {
      const aggregationKey = data.lineItems[i].aggregationKey;
      phaseIndicesByKey.set(aggregationKey, [
        ...(phaseIndicesByKey.get(aggregationKey) ?? []),
        i,
      ]);
    }

    const phaseGroups = [...phaseIndicesByKey].map(([groupKey, indices]) => ({
      groupKey,
      indices,
    }));
    const phaseSequenceErrors = findPhaseSequenceErrors(
      phaseGroups,
      data.lineItems
    );
    for (const [index, phaseError] of Object.entries(phaseSequenceErrors)) {
      if (phaseError?.start) {
        context.addIssue({
          code: 'custom',
          path: ['lineItems', Number(index), 'startOn'],
          message: phaseError.start,
        });
      }

      if (phaseError?.end) {
        context.addIssue({
          code: 'custom',
          path: ['lineItems', Number(index), 'endOn'],
          message: phaseError.end,
        });
      }
    }
  });

export type ProjectLineItemsFormData = z.infer<
  typeof projectLineItemsFormSchema
>;
